import { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronUp, FaSpinner } from 'react-icons/fa';
import { CreditAnalysis } from './types';
import appConfig from '../../../config/app-config';

const { address: CAE_API_URL, key: CAE_API_KEY } = appConfig.apis.creditAnalysisApiEnv;

interface ApprovalModalProps {
    isOpen: boolean;
    onClose: () => void;
    analysis: CreditAnalysis;
}

interface BureauData {
    bureau_name?: string;
    bureau?: string;
    status: string;
    data: Record<string, any>;
    error?: string;
}

const getStatusBadge = (status: string) => {
    const s = status?.toUpperCase();
    const map: Record<string, { style: string; label: string }> = {
        SUCCESS: { style: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Sucesso' },
        APPROVED: { style: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Aprovado' },
        ERROR: { style: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200', label: 'Erro' },
        REJECTED: { style: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Reprovado' },
        SKIPPED: { style: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', label: 'Ignorado' },
    };
    const cfg = map[s] || { style: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: status };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${cfg.style}`}>{cfg.label}</span>;
};

const tryParseJson = (value: any): any => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if ((!trimmed.startsWith('{') && !trimmed.startsWith('[')) || trimmed.length < 2) return null;
    try { return JSON.parse(trimmed); } catch { return null; }
};

const renderValue = (value: any, level = 0): JSX.Element => {
    if (value === null || value === undefined || value === '') {
        return <span className="text-gray-400 italic">—</span>;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        return renderJson(value, level);
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return <span className="text-gray-400 italic">vazio</span>;
        return (
            <div className="ml-4 space-y-1">
                {value.map((item, i) => (
                    <div key={i} className="border-l-2 border-gray-200 dark:border-slate-600 pl-2">
                        {typeof item === 'object' && item !== null ? renderJson(item, level + 1) : <span className="text-gray-900 dark:text-gray-100">{String(item)}</span>}
                    </div>
                ))}
            </div>
        );
    }

    const parsed = tryParseJson(value);
    if (parsed !== null) {
        return renderValue(parsed, level);
    }

    return <span className="text-gray-900 dark:text-gray-100">{String(value)}</span>;
};

const renderJson = (obj: any, level = 0): JSX.Element => (
    <div className={level > 0 ? 'ml-4' : ''}>
        {Object.entries(obj).map(([key, value]) => (
            <div key={key} className="py-0.5">
                {typeof value === 'object' && value !== null && !Array.isArray(value) ? (
                    <>
                        <div className="font-medium text-blue-600 dark:text-blue-400">{key}:</div>
                        {renderJson(value, level + 1)}
                    </>
                ) : (
                    <div className="flex gap-2 items-start">
                        <span className="font-medium text-gray-700 dark:text-gray-300 shrink-0">{key}:</span>
                        {renderValue(value, level)}
                    </div>
                )}
            </div>
        ))}
    </div>
);

export default function ApprovalModal({ isOpen, onClose, analysis }: ApprovalModalProps) {
    const [bureaus, setBureaus] = useState<BureauData[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedBureau, setExpandedBureau] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen || !analysis?.uuid) return;
        setLoading(true);
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (CAE_API_KEY) headers['x-api-key'] = CAE_API_KEY;
        fetch(`${CAE_API_URL}/analysis-items/${analysis.uuid}/bureau-details`, { headers })
            .then(res => res.json())
            .then(data => setBureaus(data.bureaus || []))
            .catch(() => setBureaus([]))
            .finally(() => setLoading(false));
    }, [isOpen, analysis?.uuid]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-600 px-6 py-4 z-10">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Informações de Consulta</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{analysis.drawee} — {analysis.document}</p>
                </div>

                <div className="px-6 py-4 space-y-6">
                    {/* Dados Básicos */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data</label>
                            <div className="text-gray-900 dark:text-gray-100">{analysis.date}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cedente</label>
                            <div className="text-gray-900 dark:text-gray-100">{analysis.assignor}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sacado</label>
                            <div className="text-gray-900 dark:text-gray-100">{analysis.drawee}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF/CNPJ</label>
                            <div className="text-gray-900 dark:text-gray-100">{analysis.document}</div>
                        </div>
                    </div>

                    {/* Recomendação do Sistema */}
                    {(() => {
                        const creditEngine = bureaus.find(b => (b.bureau_name || b.bureau || '').toUpperCase() === 'CREDIT_ENGINE');
                        const reasons: string[] = creditEngine?.data?.reasons || [];
                        return (
                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                                <label className="block text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">Recomendação do Sistema</label>
                                <div className="text-blue-900 dark:text-blue-200 font-semibold">
                                    {analysis.recommendation || creditEngine?.data?.recommendation || 'Análise Manual Necessária'}
                                </div>
                                {reasons.length > 0 && (
                                    <ul className="mt-2 list-disc list-inside text-sm text-blue-800 dark:text-blue-300">
                                        {reasons.map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                )}
                            </div>
                        );
                    })()}

                    {/* Bureaus — Accordions */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Resultado dos Bureaus</label>
                        {loading ? (
                            <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                                <FaSpinner className="animate-spin mr-2" /> Carregando...
                            </div>
                        ) : bureaus.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nenhum resultado disponível.</div>
                        ) : (
                            <div className="space-y-2">
                                {bureaus.filter(b => (b.bureau_name || b.bureau || '').toUpperCase() !== 'CREDIT_ENGINE').map((bureau) => {
                                    const name = bureau.bureau_name || bureau.bureau || 'Unknown';
                                    const code = name.toUpperCase().replace(/\s+/g, '_');
                                    const data = bureau.data || {};
                                    const ruleStatus = data._ruleStatus;
                                    const ruleReasons: string[] = data._ruleReasons || [];
                                    const score = data.score ?? data.optionalFeatures?.score?.score;
                                    const defaultRate = data.defaultRate ?? data.optionalFeatures?.score?.defaultRate;
                                    const rec = ruleStatus || data.recommendation || bureau.status;

                                    return (
                                        <div key={code} className="border border-gray-200 dark:border-slate-600 rounded-lg overflow-hidden">
                                            <button
                                                onClick={() => setExpandedBureau(expandedBureau === code ? null : code)}
                                                className="w-full bg-gray-50 dark:bg-slate-700 p-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-slate-600"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="font-medium text-gray-900 dark:text-gray-100">{name}</span>
                                                    {getStatusBadge(rec)}
                                                    {ruleReasons.length > 0 && (
                                                        <span className="text-xs text-red-600 dark:text-red-400">
                                                            ({ruleReasons.length} regra{ruleReasons.length > 1 ? 's' : ''})
                                                        </span>
                                                    )}
                                                    {bureau.error && <span className="text-xs text-red-500" title={bureau.error}>⚠ {bureau.error.substring(0, 60)}</span>}
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    {score !== undefined && (
                                                        <span className="text-sm text-gray-600 dark:text-gray-400">Score: <strong className="text-gray-900 dark:text-gray-100">{score}</strong></span>
                                                    )}
                                                    {defaultRate !== undefined && (() => {
                                                        const raw = String(defaultRate).replace(',', '.');
                                                        const num = parseFloat(raw);
                                                        // Serasa: per-mille integer (e.g. "00010" → 0.01); AgriScore: already decimal (e.g. "2,40" → 2.4)
                                                        const pct = raw.includes('.') || String(defaultRate).includes(',') ? num : num / 1000;
                                                        return <span className="text-sm text-gray-600 dark:text-gray-400">pDefault: <strong className="text-gray-900 dark:text-gray-100">{pct.toFixed(2)}%</strong></span>;
                                                    })()}
                                                    {expandedBureau === code ? <FaChevronUp /> : <FaChevronDown />}
                                                </div>
                                            </button>
                                            {expandedBureau === code && (
                                                <div className="p-4 bg-white dark:bg-slate-800 space-y-3">
                                                    {ruleReasons.length > 0 && (
                                                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                                                            <div className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Regras ativadas:</div>
                                                            <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-400">
                                                                {ruleReasons.map((r, i) => <li key={i}>{r}</li>)}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    <div className="bg-gray-50 dark:bg-slate-900 p-4 rounded border border-gray-200 dark:border-slate-600 text-sm font-mono overflow-x-auto max-h-80 overflow-y-auto">
                                                        {renderJson(data)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Informações Financeiras */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Limite de Crédito</label>
                            <div className="text-gray-900 dark:text-gray-100">{analysis.lcApproved ? `R$ ${parseFloat(analysis.lcApproved).toLocaleString('pt-BR')} mil` : '-'}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Exposição Atual</label>
                            <div className="text-gray-900 dark:text-gray-100">{analysis.currentExposure ? parseFloat(analysis.currentExposure).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor Original da Duplicata</label>
                            <div className="text-gray-900 dark:text-gray-100 font-semibold">
                                {analysis.originalValue ? `R$ ${parseFloat(analysis.originalValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                            </div>
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 dark:bg-slate-700 border-t border-gray-200 dark:border-slate-600 px-6 py-4 flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded hover:bg-blue-600 dark:hover:bg-blue-700">
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
}
